import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import client, { formatApiError } from "@/lib/api";
import {
  School, Plus, Upload, ChevronDown, ChevronRight, Pencil, Trash2, UserCircle2, Shield, UserPlus,
  Users, GraduationCap, Check, BarChart3, TrendingUp, ClipboardCheck, LayoutGrid, Loader2,
  MapPin, Phone, Mail,
} from "lucide-react";
import AdminPageHeader from "@/components/admin/AdminPageHeader";
import AdminCard from "@/components/admin/AdminCard";
import AdminButton from "@/components/admin/AdminButton";
import AdminModal from "@/components/admin/AdminModal";
import AdminEmptyState from "@/components/admin/AdminEmptyState";

const inputCls = "w-full px-3 py-2 rounded-xl border border-[#E5DFC8] focus:outline-none focus:border-hv-teal bg-hv-cream text-sm";

export default function AdminSchools() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [schools, setSchools] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const [formSchool, setFormSchool] = useState(null); // null = closed, {} = create, {...} = edit
  const [bulkOpen, setBulkOpen] = useState(false);
  const [sectionForm, setSectionForm] = useState(null); // {school, section|null}
  const [progressSchool, setProgressSchool] = useState(null); // school | null

  const load = useCallback(async () => {
    try {
      const { data } = await client.get("/admin/schools");
      setSchools(data.schools);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
      setSchools([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // ?prefill=<inquiry_id> from the admin Inbox's Inquiries tab — opens Create
  // pre-filled, never auto-submits (an inquiry is just a lead, not a school yet).
  useEffect(() => {
    const inquiryId = searchParams.get("prefill");
    if (!inquiryId) return;
    client.get("/admin/school-inquiries").then(({ data }) => {
      const inquiry = data.inquiries.find((i) => i.id === inquiryId);
      if (inquiry) {
        setFormSchool({
          name: inquiry.school_name, max_learners: inquiry.estimated_student_count || 30,
          contact_email: inquiry.contact_email, contact_phone: inquiry.contact_phone,
          city_country: [inquiry.city, inquiry.country].filter(Boolean).join(", "),
          address: inquiry.address || "",
        });
      }
    }).finally(() => {
      searchParams.delete("prefill");
      setSearchParams(searchParams, { replace: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleExpanded = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const removeSchool = async (school) => {
    if (!window.confirm(`Delete ${school.name}? This only works if it has no sections or students left.`)) return;
    try {
      await client.delete(`/admin/schools/${school.id}`);
      setMsg(`Deleted ${school.name}.`);
      load();
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  };

  const removeSection = async (section) => {
    if (!window.confirm(`Delete this section? This only works if it has no students left.`)) return;
    try {
      await client.delete(`/admin/sections/${section.id}`);
      setMsg("Section deleted.");
      load();
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  };

  return (
    <div data-testid="admin-schools">
      <AdminPageHeader
        icon={School}
        title="Schools"
        subtitle="Create a school, set how many students it can hold, and add one or more teachers to it."
        actions={
          <>
            <AdminButton variant="secondary" icon={Upload} onClick={() => setBulkOpen(true)} data-testid="admin-schools-bulk-toggle">
              Bulk import
            </AdminButton>
            <AdminButton variant="primary" icon={Plus} onClick={() => setFormSchool({})} data-testid="admin-schools-create-toggle">
              Create school
            </AdminButton>
          </>
        }
      />

      {err && <div className="mt-4 text-hv-coral font-bold text-sm" data-testid="admin-schools-error">{err}</div>}
      {msg && <div className="mt-4 text-hv-teal font-bold text-sm break-all" data-testid="admin-schools-msg">{msg}</div>}

      {schools === null ? (
        <p className="mt-6 text-hv-slate text-sm">Loading…</p>
      ) : schools.length === 0 ? (
        <div className="mt-6"><AdminEmptyState icon={School} title="No schools yet." subtitle="Create one, or import a submitted roster from the Inbox's School Onboarding tab." /></div>
      ) : (
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 items-start">
          {schools.map((school) => (
            <SchoolCard
              key={school.id}
              school={school}
              expanded={!!expanded[school.id]}
              onToggle={() => toggleExpanded(school.id)}
              onEdit={() => setFormSchool(school)}
              onDelete={() => removeSchool(school)}
              onViewProgress={() => setProgressSchool(school)}
              onAddSection={() => setSectionForm({ school, section: null })}
              onEditSection={(section) => setSectionForm({ school, section })}
              onDeleteSection={removeSection}
              refreshKey={msg}
            />
          ))}
        </div>
      )}

      {formSchool !== null && (
        <SchoolFormModal
          school={formSchool}
          onClose={() => setFormSchool(null)}
          onSaved={(m) => { setMsg(m); setFormSchool(null); load(); }}
          onError={(e) => setErr(e)}
        />
      )}

      {bulkOpen && (
        <BulkImportModal
          onClose={() => setBulkOpen(false)}
          onDone={(m) => { setMsg(m); setBulkOpen(false); load(); }}
        />
      )}

      {sectionForm && (
        <SectionFormModal
          school={sectionForm.school}
          section={sectionForm.section}
          onClose={() => setSectionForm(null)}
          onSaved={(m) => { setMsg(m); setSectionForm(null); load(); }}
        />
      )}

      {progressSchool && (
        <SchoolProgressModal school={progressSchool} onClose={() => setProgressSchool(null)} />
      )}
    </div>
  );
}

function SchoolCard({ school, expanded, onToggle, onEdit, onDelete, onViewProgress, onAddSection, onEditSection, onDeleteSection, refreshKey }) {
  const [sections, setSections] = useState(null);

  const loadSections = useCallback(async () => {
    try {
      const { data } = await client.get(`/admin/schools/${school.id}/sections`);
      setSections(data.sections);
    } catch {
      setSections([]);
    }
  }, [school.id]);

  useEffect(() => { if (expanded) loadSections(); }, [expanded, loadSections, refreshKey]);

  const navigate = useNavigate();
  const full = school.seats_left <= 0;
  const hasContact = school.city_country || school.contact_phone || school.contact_email;

  return (
    <AdminCard padding="sm" data-testid={`school-card-${school.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-10 h-10 rounded-xl bg-hv-tealTint text-hv-teal flex items-center justify-center shrink-0">
            <School size={20} />
          </span>
          <div className="hv-heading text-hv-navy text-lg truncate">{school.name}</div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <AdminButton iconOnly icon={Pencil} title="Edit school" onClick={onEdit} />
          <AdminButton iconOnly icon={Trash2} title="Delete school" onClick={onDelete} />
        </div>
      </div>

      <div className="mt-3 space-y-2 text-sm">
        <div className="flex items-center gap-2">
          <Shield size={15} className="text-hv-slate shrink-0" />
          {school.principal_name ? (
            <span className="truncate"><span className="text-hv-navy font-semibold">{school.principal_name}</span> <span className="text-hv-slate">· Principal</span></span>
          ) : (
            <span className="text-hv-slate italic">No principal yet</span>
          )}
        </div>
        {school.teacher_guardian_id && (
          <div className="flex items-center gap-2 text-hv-navy font-semibold">
            <GraduationCap size={15} className="text-hv-slate shrink-0" /> 1 direct teacher (no sections)
          </div>
        )}
        <div className="flex items-start gap-2">
          <Users size={15} className="text-hv-slate shrink-0 mt-0.5" />
          <span className={`font-semibold ${full ? "text-hv-coral" : "text-hv-navy"}`}>
            {school.allocated_seats} / {school.max_learners} seats allocated to sections{full ? " · full" : ` · ${school.seats_left} left`}
          </span>
        </div>
      </div>

      <button type="button" onClick={onViewProgress} data-testid={`school-card-progress-${school.id}`}
              className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-xl bg-hv-tealTint hover:brightness-95 py-2 text-xs font-bold text-hv-navy">
        <BarChart3 size={14} /> View progress
      </button>
      <button type="button" onClick={() => navigate(`/admin/school-progress?school=${school.id}`)} data-testid={`school-card-analytics-${school.id}`}
              className="mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-xl border border-[#E5DFC8] hover:bg-hv-cream py-2 text-xs font-bold text-hv-navy">
        <TrendingUp size={14} /> Full analytics &amp; reports
      </button>

      {hasContact && (
        <div className="mt-3 pt-3 border-t border-[#E5DFC8] space-y-1 text-xs text-hv-slate">
          {school.city_country && <p className="flex items-center gap-2 truncate"><MapPin size={13} className="shrink-0" />{school.city_country}</p>}
          {school.contact_phone && <p className="flex items-center gap-2 truncate"><Phone size={13} className="shrink-0" />{school.contact_phone}</p>}
          {school.contact_email && <p className="flex items-center gap-2 truncate"><Mail size={13} className="shrink-0" />{school.contact_email}</p>}
        </div>
      )}

      {!school.teacher_guardian_id && (
        <button onClick={onToggle} className="mt-3 pt-3 border-t border-[#E5DFC8] w-full flex items-center justify-between text-xs font-bold uppercase tracking-wide text-hv-slate hover:text-hv-navy"
                data-testid={`school-card-toggle-${school.id}`}>
          {school.section_count === 0 ? "Sections" : `${school.section_count} section${school.section_count === 1 ? "" : "s"}`}
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
      )}

      {expanded && !school.teacher_guardian_id && (
        <div className="mt-2 space-y-2" data-testid={`school-sections-${school.id}`}>
          {sections === null ? (
            <p className="text-hv-slate text-xs">Loading…</p>
          ) : sections.length === 0 ? (
            <p className="text-hv-slate text-xs italic">No sections yet.</p>
          ) : (
            sections.map((s) => (
              <div key={s.id} className="flex items-center justify-between gap-2 rounded-xl bg-hv-cream px-3 py-2">
                <div className="min-w-0">
                  <div className="text-hv-navy text-xs font-bold truncate">{s.class_name} {s.name ? `· ${s.name}` : ""}</div>
                  <div className="text-hv-slate text-[11px]">
                    {s.teacher_name || <span className="italic">Unassigned</span>} · {s.student_count}/{s.max_learners}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <AdminButton iconOnly size="sm" icon={Pencil} title="Edit section" onClick={() => onEditSection(s)} />
                  <AdminButton iconOnly size="sm" icon={Trash2} title="Delete section" onClick={() => onDeleteSection(s)} />
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {!school.teacher_guardian_id && (
        <button type="button" onClick={onAddSection} disabled={full}
                title={full ? "No seats left in this school's pool" : undefined}
                data-testid={`school-card-add-teacher-${school.id}`}
                className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-[#E5DFC8] hover:bg-hv-cream py-2 text-xs font-bold text-hv-navy disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent">
          <UserPlus size={14} /> Add a teacher
        </button>
      )}
    </AdminCard>
  );
}

function SchoolFormModal({ school, onClose, onSaved, onError }) {
  const isEdit = !!school.id;
  const [form, setForm] = useState({
    name: school.name || "", max_learners: school.max_learners || 30,
    contact_email: school.contact_email || "", contact_phone: school.contact_phone || "",
    city_country: school.city_country || "", address: school.address || "",
  });
  const [principalEmail, setPrincipalEmail] = useState("");
  const [principalLookup, setPrincipalLookup] = useState(null); // {found, name, role} | null
  const [directTeacherId, setDirectTeacherId] = useState(school.teacher_guardian_id || "");
  const [directTeachers, setDirectTeachers] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const hasSections = isEdit && school.section_count > 0;

  // 250ms-debounced live lookup, matching AdminUsers.js's search debounce.
  useEffect(() => {
    const email = principalEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) { setPrincipalLookup(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await client.get(`/admin/users?q=${encodeURIComponent(email)}&limit=5`);
        const match = data.users.find((u) => u.email.toLowerCase() === email);
        setPrincipalLookup(match ? { found: true, name: match.name, role: match.role } : { found: false });
      } catch { setPrincipalLookup(null); }
    }, 250);
    return () => clearTimeout(t);
  }, [principalEmail]);

  // The direct-teacher picker only matters for a school with no sections —
  // a school already running sections manages its teachers there instead
  // (legacy-direct and sectioned are mutually exclusive, enforced server-side too).
  useEffect(() => {
    if (hasSections) { setDirectTeachers([]); return; }
    const params = new URLSearchParams({ mode: "direct" });
    if (isEdit) params.set("exclude_school_id", school.id);
    client.get(`/admin/teachers/available?${params.toString()}`).then(({ data }) => setDirectTeachers(data.teachers)).catch(() => setDirectTeachers([]));
  }, [hasSections, isEdit, school.id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSaving(true);
    try {
      const payload = {
        name: form.name, max_learners: Number(form.max_learners),
        contact_email: form.contact_email || null, contact_phone: form.contact_phone || null,
        city_country: form.city_country || null, address: form.address || null,
      };
      if (!hasSections) payload.teacher_guardian_id = directTeacherId || null;
      // principal_email is only ever included when the admin actually typed
      // something — an untouched field must leave the current principal
      // alone, never clear her (that's what the separate Remove button is for).
      if (principalEmail.trim()) payload.principal_email = principalEmail.trim().toLowerCase();

      if (isEdit) {
        await client.put(`/admin/schools/${school.id}`, payload);
      } else {
        await client.post("/admin/schools", payload);
      }
      onSaved(`${isEdit ? "Updated" : "Created"} ${form.name}.`);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setSaving(false);
    }
  };

  const removePrincipal = async () => {
    if (!window.confirm(`Remove ${school.principal_name || "the principal"} from ${school.name}?`)) return;
    setSaving(true);
    setErr("");
    try {
      await client.put(`/admin/schools/${school.id}`, { remove_principal: true });
      onSaved(`Removed the principal from ${school.name}.`);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
      setSaving(false);
    }
  };

  return (
    <AdminModal open onClose={onClose} title={isEdit ? `Edit ${school.name}` : "Create school"} maxWidth="max-w-lg" testId="school-form-modal">
      <form onSubmit={submit} className="space-y-3">
        <input required placeholder="School name" value={form.name} onChange={(e) => set("name", e.target.value)}
               className={inputCls} data-testid="school-form-name" />
        <div className="grid grid-cols-2 gap-3">
          <input required type="number" min="1" placeholder="Max seats" value={form.max_learners}
                 onChange={(e) => set("max_learners", e.target.value)} className={inputCls} data-testid="school-form-max-learners" />
          <input placeholder="Contact phone" value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} className={inputCls} />
        </div>
        <input type="email" placeholder="Contact email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} className={inputCls} />
        <div className="grid grid-cols-2 gap-3">
          <input placeholder="City, Country" value={form.city_country} onChange={(e) => set("city_country", e.target.value)} className={inputCls} />
          <input placeholder="Address" value={form.address} onChange={(e) => set("address", e.target.value)} className={inputCls} />
        </div>

        <div>
          <label className="text-xs font-bold text-hv-navy uppercase tracking-wider">Direct teacher (optional)</label>
          {hasSections ? (
            <p className="text-xs text-hv-slate mt-1.5">
              This school already has sections — manage its teachers per-section instead of a single direct teacher.
            </p>
          ) : (
            <select value={directTeacherId} onChange={(e) => setDirectTeacherId(e.target.value)} className={`${inputCls} mt-1.5`} data-testid="school-form-direct-teacher">
              <option value="">None — I'll add sections instead</option>
              {(directTeachers || []).map((t) => <option key={t.id} value={t.id}>{t.name} ({t.email})</option>)}
            </select>
          )}
        </div>

        <div className="pt-1">
          <label className="text-xs font-bold text-hv-navy uppercase tracking-wider">Principal</label>
          {isEdit && school.principal_name && (
            <div className="flex items-center justify-between mt-1.5 rounded-xl bg-hv-tealTint px-3 py-2">
              <span className="text-sm text-hv-navy font-semibold inline-flex items-center gap-1.5">
                <UserCircle2 size={14} /> {school.principal_name} ({school.principal_email})
              </span>
              <button type="button" onClick={removePrincipal} className="text-hv-coral text-xs font-bold hover:underline" data-testid="school-form-remove-principal">
                Remove
              </button>
            </div>
          )}
          <input type="email" placeholder={isEdit && school.principal_name ? "Replace with a different email…" : "Existing account's email…"}
                 value={principalEmail} onChange={(e) => setPrincipalEmail(e.target.value)}
                 className={`${inputCls} mt-1.5`} data-testid="school-form-principal-email" />
          {principalLookup && (
            <p className={`text-xs mt-1 ${principalLookup.found ? "text-hv-teal" : "text-hv-coral"}`}>
              {principalLookup.found
                ? `Found: ${principalLookup.name} (currently ${principalLookup.role})`
                : "Not found — create her account first (role: Student), then assign her here."}
            </p>
          )}
        </div>

        {err && <div className="text-hv-coral font-bold text-sm">{err}</div>}

        <div className="flex justify-end gap-2 pt-2">
          <AdminButton type="button" variant="secondary" onClick={onClose}>Cancel</AdminButton>
          <AdminButton type="submit" variant="primary" disabled={saving} data-testid="school-form-submit">
            {saving ? "Saving…" : isEdit ? "Save" : "Create"}
          </AdminButton>
        </div>
      </form>
    </AdminModal>
  );
}

function SectionFormModal({ school, section, onClose, onSaved }) {
  const isEdit = !!section;
  const [form, setForm] = useState({
    name: section?.name || "", class_name: section?.class_name || "", max_learners: section?.max_learners || 20,
  });
  const [teachers, setTeachers] = useState(null);
  const [teacherId, setTeacherId] = useState(section?.teacher_guardian_id || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    const params = new URLSearchParams({ mode: "section" });
    if (isEdit) params.set("exclude_section_id", section.id);
    client.get(`/admin/teachers/available?${params.toString()}`).then(({ data }) => setTeachers(data.teachers)).catch(() => setTeachers([]));
  }, [isEdit, section]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSaving(true);
    try {
      const payload = {
        name: form.name, class_name: form.class_name, max_learners: Number(form.max_learners),
        teacher_guardian_id: teacherId || null,
      };
      if (isEdit) {
        await client.put(`/admin/sections/${section.id}`, payload);
      } else {
        await client.post("/admin/sections", { ...payload, school_id: school.id });
      }
      onSaved(`${isEdit ? "Updated" : "Created"} section.`);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminModal open onClose={onClose} title={isEdit ? "Edit section" : `Add a section to ${school.name}`} testId="section-form-modal">
      <form onSubmit={submit} className="space-y-3">
        <p className="text-xs text-hv-slate">{school.seats_left} seat{school.seats_left === 1 ? "" : "s"} left in {school.name}'s pool.</p>
        <div className="grid grid-cols-2 gap-3">
          <input required placeholder="Class (e.g. Grade 2)" value={form.class_name} onChange={(e) => set("class_name", e.target.value)}
                 className={inputCls} data-testid="section-form-class" />
          <input required placeholder="Section (e.g. A)" value={form.name} onChange={(e) => set("name", e.target.value)}
                 className={inputCls} data-testid="section-form-name" />
        </div>
        <input required type="number" min="1" placeholder="Max seats" value={form.max_learners} onChange={(e) => set("max_learners", e.target.value)}
               className={inputCls} data-testid="section-form-max-learners" />

        <div>
          <label className="text-xs font-bold text-hv-navy uppercase tracking-wider">Teacher</label>
          <select value={teacherId} onChange={(e) => setTeacherId(e.target.value)} className={`${inputCls} mt-1.5`} data-testid="section-form-teacher">
            <option value="">Unassigned</option>
            {(teachers || []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.email}){t.running_sections_count > 0 ? ` — already runs ${t.running_sections_count} other section${t.running_sections_count === 1 ? "" : "s"}` : ""}
              </option>
            ))}
          </select>
        </div>

        {err && <div className="text-hv-coral font-bold text-sm">{err}</div>}

        <div className="flex justify-end gap-2 pt-2">
          <AdminButton type="button" variant="secondary" onClick={onClose}>Cancel</AdminButton>
          <AdminButton type="submit" variant="primary" disabled={saving} data-testid="section-form-submit">
            {saving ? "Saving…" : isEdit ? "Save" : "Create"}
          </AdminButton>
        </div>
      </form>
    </AdminModal>
  );
}

function BulkImportModal({ onClose, onDone }) {
  const [schoolName, setSchoolName] = useState("");
  const [teacherId, setTeacherId] = useState("");
  const [maxLearners, setMaxLearners] = useState("");
  const [file, setFile] = useState(null);
  const [teachers, setTeachers] = useState(null);
  const [importing, setImporting] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    client.get("/admin/teachers/available?mode=direct").then(({ data }) => setTeachers(data.teachers)).catch(() => setTeachers([]));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { setErr("Choose a .xlsx file first."); return; }
    setErr("");
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("school_name", schoolName);
      formData.append("teacher_id", teacherId);
      if (maxLearners) formData.append("max_learners", maxLearners);
      const { data } = await client.post("/admin/schools/bulk-import", formData, { headers: { "Content-Type": undefined } });
      setResult(data);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setImporting(false);
    }
  };

  return (
    <AdminModal open onClose={onClose} title="Bulk import a class roster" maxWidth="max-w-lg" testId="bulk-import-modal">
      {result ? (
        <div className="space-y-3" data-testid="bulk-import-result">
          <p className="text-hv-teal font-bold text-sm inline-flex items-center gap-1.5">
            <Check size={16} strokeWidth={3} /> {result.created} student{result.created === 1 ? "" : "s"} imported.
          </p>
          {result.errors.length > 0 && (
            <div>
              <p className="text-xs font-bold text-hv-coral uppercase tracking-wider mb-1">{result.errors.length} row{result.errors.length === 1 ? "" : "s"} skipped</p>
              <ul className="text-xs text-hv-slate space-y-0.5 max-h-40 overflow-y-auto">
                {result.errors.map((e, i) => <li key={i}>Row {e.row}: {e.reason}</li>)}
              </ul>
            </div>
          )}
          <div className="flex justify-end pt-2">
            <AdminButton variant="primary" onClick={() => onDone(`Imported ${result.created} students.`)}>Done</AdminButton>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <p className="text-hv-slate text-sm">
            One school, one already-real teacher, a flat student list. Fixed layout: header row, then
            Name | Age | Gender | Class (optional) | Section (optional) | Parent Mobile (optional).
          </p>
          <input required placeholder="New school name" value={schoolName} onChange={(e) => setSchoolName(e.target.value)}
                 className={inputCls} data-testid="bulk-import-school-name" />
          <select required value={teacherId} onChange={(e) => setTeacherId(e.target.value)} className={inputCls} data-testid="bulk-import-teacher">
            <option value="">Choose the receiving teacher…</option>
            {(teachers || []).map((t) => <option key={t.id} value={t.id}>{t.name} ({t.email})</option>)}
          </select>
          <input type="number" min="1" placeholder="Total seats (defaults to roster size)" value={maxLearners}
                 onChange={(e) => setMaxLearners(e.target.value)} className={inputCls} />
          <input required type="file" accept=".xlsx" onChange={(e) => setFile(e.target.files?.[0] || null)}
                 className="w-full text-sm" data-testid="bulk-import-file" />

          {err && <div className="text-hv-coral font-bold text-sm">{err}</div>}

          <div className="flex justify-end gap-2 pt-2">
            <AdminButton type="button" variant="secondary" onClick={onClose}>Cancel</AdminButton>
            <AdminButton type="submit" variant="primary" disabled={importing} data-testid="bulk-import-submit">
              {importing ? "Importing…" : "Import"}
            </AdminButton>
          </div>
        </form>
      )}
    </AdminModal>
  );
}

// A read-only, per-school progress quick-look — ported from the sibling
// Little Verses app's SchoolProgressDialog: click "View progress" on any
// school card to see the same numbers its own principal sees, without
// leaving this page for the full School Progress view (which needs a
// school picked from a dropdown and shows a lot more than a quick glance
// needs). Reuses GET /principal/overview and /principal/teachers exactly
// as-is with an explicit school_id — both already admin-callable (see
// _resolve_principal_scope in server.py) — rather than adding a parallel
// admin-only endpoint that could quietly drift from what a principal sees
// for the same school.
function SchoolProgressModal({ school, onClose }) {
  const [overview, setOverview] = useState(null);
  const [teachers, setTeachers] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    setOverview(null);
    setTeachers(null);
    setErr("");
    Promise.all([
      client.get("/principal/overview", { params: { school_id: school.id } }),
      client.get("/principal/teachers", { params: { school_id: school.id } }),
    ]).then(([overviewRes, teachersRes]) => {
      setOverview(overviewRes.data);
      setTeachers(teachersRes.data.teachers);
    }).catch((ex) => setErr(formatApiError(ex.response?.data?.detail) || ex.message));
  }, [school.id]);

  const busy = !overview && !err;
  const attendanceKnown = overview && overview.attendance_today_pct !== null && overview.attendance_today_pct !== undefined;
  const stats = overview ? [
    { icon: GraduationCap, value: overview.teacher_count, label: "Teachers" },
    { icon: Users, value: overview.student_count, label: "Students" },
    { icon: LayoutGrid, value: overview.class_count, label: "Classes" },
    { icon: ClipboardCheck, value: attendanceKnown ? `${overview.attendance_today_pct}%` : "—", label: "Attendance today" },
    { icon: TrendingUp, value: `${overview.average_progress_pct}%`, label: "Avg. progress" },
  ] : [];

  return (
    <AdminModal open onClose={onClose} title={school.name} maxWidth="max-w-lg" testId="school-progress-modal">
      <p className="text-hv-slate text-xs -mt-2 mb-4">How this school's students are actually doing — same numbers its principal sees, read-only.</p>

      {err && <div className="text-hv-coral font-bold text-sm" data-testid="school-progress-error">{err}</div>}

      {busy ? (
        <div className="flex justify-center py-10">
          <Loader2 className="w-6 h-6 text-hv-slate animate-spin" strokeWidth={2.5} />
        </div>
      ) : overview && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {stats.map((s) => (
              <div key={s.label} className="rounded-xl bg-hv-cream px-3 py-2.5 text-center">
                <s.icon className="w-4 h-4 text-hv-teal mx-auto" strokeWidth={2.5} />
                <div className="hv-heading text-hv-navy text-lg mt-1">{s.value}</div>
                <div className="text-[10px] uppercase tracking-wider text-hv-slate font-bold mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>

          <div>
            <div className="text-xs font-bold text-hv-navy uppercase tracking-wider mb-1.5">Teachers ({teachers?.length || 0})</div>
            {!teachers?.length ? (
              <p className="text-hv-slate text-sm italic">No teachers assigned yet.</p>
            ) : (
              <div className="space-y-1.5" data-testid="school-progress-teachers">
                {teachers.map((t) => (
                  <div key={t.teacher.id} className="flex items-center justify-between gap-2 rounded-xl bg-hv-cream px-3 py-2 text-sm">
                    <div className="min-w-0">
                      <div className="text-hv-navy font-bold truncate">{t.teacher.name || t.teacher.email}</div>
                      <div className="text-hv-slate text-[11px]">{t.student_count} student{t.student_count === 1 ? "" : "s"}</div>
                    </div>
                    <div className="text-hv-teal font-bold shrink-0">{t.avg_progress_pct}%</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </AdminModal>
  );
}
